import { YPaymentAmount, YPaymentCurrency } from '../y/amount';
import { $api } from '../y/api';
import { $fn } from '../y/fn';
import { YDate } from '../y/time';
import { YCountryId, YDateString, YId, YMonthSpec, YTimeString } from '../y/types';
import { CProducerId } from './c-producer';

export type EPayoutDestinationKind = 'bankgiro' | 'plusgiro' | 'bankaccount' | 'iban';

export interface EPayoutDestination {
    kind: EPayoutDestinationKind;
    number: string;
}

export interface TLMonthRevenue {
    month: YMonthSpec;
    revenue: { [country in YCountryId]: YPaymentAmount };
    used_value: { [country in YCountryId]: YPaymentAmount };
    global_ratio: { [country in YCountryId]: number };
}

export type EPayableKind = 'custom' | 'session_tickets' | 'video_views' | 'video_ppv';

export interface EPendingPayableItem {
    payable: YId;
    payable_kind: EPayableKind;
    description: string;
    extra_info: string;
    date: YDateString;
    vat: number;
    quantity: number;
    value: YPaymentAmount;
    amount: YPaymentAmount;
    mg_amount: YPaymentAmount;
    adjustment: number;
    is_fixed: boolean;
    is_disabled: boolean;
}

export interface EPendingPayable {
    country: YCountryId;
    currency: YPaymentCurrency;
    month: YMonthSpec;
    producer_id: CProducerId;
    payout_type: EPayoutType;
    is_generated: boolean;
    items: EPendingPayableItem[];
}

export interface EGeneratePayoutsRequestPayment {
    month: YMonthSpec;
    payable_ids: YId[];
}

export interface EMinimumGuarantee {
    id: YId;
    video_id: YId;
    amount_total: YPaymentAmount;
    amount_available: YPaymentAmount;
    from: YTimeString;
    to: YTimeString;
}

export type EBankTransferItemKind = 'producer';

export interface EBankTransferItem {
    bank_transfer_id: YId;
    kind: EBankTransferItemKind
    producer_payout_id?: YId;
    amount: YPaymentAmount;
    destination: EPayoutDestination;
    descriptor: string;
}

export interface EBankTransfer {
    id: YId;
    country: YCountryId;
    paid_on: YDateString;
    created_at: YTimeString;
    items: EBankTransferItem[];
}

export type EPayoutSection = 'event' | 'vod' | 'ppv' | 'other';

export interface EPayoutItem {
    id: YId;
    producer_payout_id: YId;
    payable_id: YId;
    minimum_guarantee_usage_id?: YId;
    quantity: number;
    amount_total: YPaymentAmount;
    amount_payout: YPaymentAmount;
    vat: number;
    section: EPayoutSection;
    date: YDateString;
    time?: YTimeString;
    description: string;
}

export type EPayoutType = 'transfer' | 'invoice';

export interface EProducerPayoutListView {
    id: YId;
    month: YMonthSpec;
    number: number;
    producer_id: CProducerId;
    payout_type: EPayoutType;
    quantity: number;
    amount: YPaymentAmount;
    sent_at?: YTimeString;
    paid_on?: YDateString;
    bank_transfer_id?: YId;
}

export interface EPayablesPendingQuery {
    country: YCountryId;
    month?: YMonthSpec;
    producer?: YId;
}

export interface EMonthInfo {
    month: YMonthSpec;
    country: YCountryId;
    revenue: YPaymentAmount;
    used_value: YPaymentAmount;
    global_ratio: number;
}

export interface EPayablesPendingResponse {
    infos: Record<YMonthSpec, EMonthInfo>;
    payouts: EPendingPayable[];
}

export interface EPayableUpdate {
    producer_id?: YId;
    is_disabled?: boolean;
    vat?: number;
    price_fixed?: YPaymentAmount;
    adjustment?: number;
    description?: string;
}

export function ePendingPayablesGet(query: EPayablesPendingQuery) {
    let url = 'e/payables/pending'

    url += '?country=' + query.country;

    if(query.producer)
        url += '&producer=' + query.producer;

    if(query.month)
        url += '&month=' + query.month;

    return $api.GET<EPayablesPendingResponse>(url);
}

export function eBankTransfersGet() {
    return $api.GET<EBankTransfer[]>('e/bank_transfers');
}

export function eProducerPayoutsList(country: YCountryId, month?: YMonthSpec, producer_id?: YId) {
    let url = 'e/producer_payouts/list?country=' + country;
    if(month && month != 'all') {
        url += '&month=' + month;
    }
    if(producer_id && producer_id != 'all') {
        url += '&producer_id=' + producer_id;
    }
    return $api.GET<EProducerPayoutListView[]>(url);
}

export function ePayablesUpdate(payable_ids: YId[], update: EPayableUpdate, query: EPayablesPendingQuery) {
    return $api.POST<EPayablesPendingResponse>('e/payables/update', { payable_ids, update, query });
}

export function ePayablesSetGlobalRatio(month: YMonthSpec, country: YCountryId, ratio: number, query: EPayablesPendingQuery) {
    return $api.POST<EPayablesPendingResponse>('e/payables/set_global_ratio', { month, country, ratio, query });
}

export function eProducerPayoutsGenerate(date: YDate, payouts: EGeneratePayoutsRequestPayment[], query: EPayablesPendingQuery) {
    return $api.POST<EPayablesPendingResponse>('e/producer_payouts/generate', { date, payouts, query });
}

export function eProducerPayoutGetItems(payout_id: YId) {
    return $api.GET<EPayoutItem[]>(`e/producer_payout/${payout_id}/items`);
}

export function eProducerPayoutsSend(payout_ids: YId[], send_email: boolean) {
    return $api.POST<EProducerPayoutListView[]>('e/producer_payouts/send', { producer_payout_ids: payout_ids, send_email: send_email });
}

export function eProducerPayoutUpdate(id: YId, update: any) {
    return $api.PUT<EProducerPayoutListView>(`e/producer_payout/${id}`, update);
}

export function eProducerPayoutDelete(id: YId) {
    return $api.DELETE(`e/producer_payout/${id}`);
}

export function eBankTransferDelete(id: YId) {
    return $api.DELETE(`e/bank_transfer/${id}`);
}

export function eBankTransferPaid(id: YId, date: YDate) {
    return $api.POST(`e/bank_transfer/${id}/set_paid`, { date: date }).then($fn.TRUE);
}

export function eBankTransferCreate(producer_payout_ids: YId[]) {
    return $api.POST<EBankTransfer>('e/bank_transfers/create', { producer_payout_ids });
}

export function eMinimumGuaranteesByEvent(event: YId) {
    return $api.GET<EMinimumGuarantee[]>('event/' + event + '/minimum_guarantees');
}

export function eMinimumGuaranteeCreate(event: YId, data: any) {
    return $api.POST<EMinimumGuarantee[]>('event/' + event + '/minimum_guarantee', data);
}
